/**
 * money.js — exact currency arithmetic.
 *
 * WHY THIS EXISTS
 * The v1 codebase stored and computed every amount as a JavaScript float (and
 * frequently as a *string* straight out of an <input>). That produced:
 *   - totals that drift by fractions of a paisa and never compare equal to zero,
 *     so fully-paid invoices kept showing "Receive Payment";
 *   - `parseFloat(total + doc.data().closingBalance)` string-concatenation bugs;
 *   - CGST/SGST halves that did not add back up to the total tax.
 *
 * THE RULE IN v2: money is an INTEGER NUMBER OF PAISE. Never a float, never a
 * string. Convert at the edges (user input in, display out) and nowhere else.
 * Firestore stores paise integers. 1 rupee === 100 paise.
 *
 * Integers are exact in IEEE-754 up to 2^53, i.e. ~90,071,992,547,409 rupees.
 * That ceiling is not reachable by an SMB billing product.
 */

export const PAISE_PER_RUPEE = 100;

/** Largest amount we accept, in paise (₹10,000 crore). Guards typo'd input. */
const MAX_PAISE = 100_000_000_00 * 100;

/**
 * Coerce anything user- or legacy-supplied into integer paise.
 * Accepts: number (rupees), numeric string, null/undefined/'' -> 0.
 * Rejects NaN/Infinity by returning 0 rather than poisoning downstream math —
 * v1 let `parseFloat("abc")` become NaN and permanently corrupt an item's stock.
 */
export function toPaise(rupees) {
  if (rupees === null || rupees === undefined || rupees === '') return 0;
  if (typeof rupees === 'bigint') return Number(rupees) * PAISE_PER_RUPEE;

  let n;
  if (typeof rupees === 'number') {
    n = rupees;
  } else {
    // Strip grouping separators, currency symbols and stray spaces.
    const cleaned = String(rupees).replace(/[₹,\s]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') return 0;
    n = Number(cleaned);
  }

  if (!Number.isFinite(n)) return 0;

  // Round half-away-from-zero at the paisa. Using Math.round on a scaled value
  // hits the classic 1.005 -> 100.49999 problem, so nudge with an epsilon that
  // is far below a paisa but above float noise.
  const scaled = n * PAISE_PER_RUPEE;
  const p = scaled < 0
    ? -Math.round(Math.abs(scaled) + Number.EPSILON * Math.abs(scaled))
    : Math.round(scaled + Number.EPSILON * scaled);

  if (!Number.isFinite(p)) return 0;
  if (Math.abs(p) > MAX_PAISE) return p < 0 ? -MAX_PAISE : MAX_PAISE;
  return p;
}

/** Integer paise -> number of rupees. For display and for `to-words` only. */
export function toRupees(paise) {
  return round(paise) / PAISE_PER_RUPEE;
}

/** Defensive integer coercion for values read back out of Firestore. */
export function round(paise) {
  if (!Number.isFinite(paise)) return 0;
  return Math.round(paise);
}

export const add = (...amounts) => amounts.reduce((acc, a) => acc + round(a), 0);

export const subtract = (a, b) => round(a) - round(b);

export const negate = (a) => -round(a);

/** Multiply paise by a plain number (a quantity), rounding to whole paise. */
export function multiply(paise, factor) {
  const f = Number(factor);
  if (!Number.isFinite(f)) return 0;
  return round(round(paise) * f);
}

/**
 * Apply a percentage to an amount, e.g. percentOf(10000, 18) === 1800.
 * Rounds half-up to the paisa. Percentages themselves are plain numbers
 * (18, 2.5, 0.25) — they are ratios, not money, so floats are fine here.
 */
export function percentOf(paise, percent) {
  const p = Number(percent);
  if (!Number.isFinite(p) || p === 0) return 0;
  return round((round(paise) * p) / 100);
}

/**
 * Split an amount into `parts` shares that sum EXACTLY back to the original.
 * This is what makes CGST/SGST correct: v1 rendered `tax / 2` twice, so an odd
 * paisa of tax vanished or duplicated. Remainder paise go to the earliest
 * shares, which is the convention Indian accounting software uses.
 */
export function split(paise, parts = 2) {
  const total = round(paise);
  const n = Math.max(1, Math.trunc(parts));
  const sign = total < 0 ? -1 : 1;
  const abs = Math.abs(total);
  const base = Math.floor(abs / n);
  let remainder = abs - base * n;

  const shares = [];
  for (let i = 0; i < n; i += 1) {
    let share = base;
    if (remainder > 0) {
      share += 1;
      remainder -= 1;
    }
    shares.push(share * sign);
  }
  return shares;
}

/**
 * Distribute `amount` across `weights` proportionally, preserving the total
 * exactly (largest-remainder method). Used to push an invoice-level discount
 * down onto individual line items so each line's taxable value is correct
 * BEFORE tax is computed — the single biggest tax bug in v1.
 */
export function allocate(paise, weights) {
  const total = round(paise);
  const w = weights.map((x) => (Number.isFinite(Number(x)) ? Math.abs(Number(x)) : 0));
  const weightSum = w.reduce((a, b) => a + b, 0);

  if (w.length === 0) return [];
  if (weightSum === 0) {
    // No basis to allocate on: put everything on the first line.
    return w.map((_, i) => (i === 0 ? total : 0));
  }

  const raw = w.map((x) => (total * x) / weightSum);
  const floored = raw.map((x) => (total < 0 ? Math.ceil(x) : Math.floor(x)));
  let remainder = total - floored.reduce((a, b) => a + b, 0);

  // Hand out the leftover paise to the largest fractional parts first.
  const order = raw
    .map((x, i) => ({ i, frac: Math.abs(x - floored[i]) }))
    .sort((a, b) => b.frac - a.frac);

  const step = total < 0 ? -1 : 1;
  let k = 0;
  while (remainder !== 0 && k < order.length * 2) {
    floored[order[k % order.length].i] += step;
    remainder -= step;
    k += 1;
  }
  return floored;
}

/**
 * Round an amount to the nearest rupee and report the adjustment.
 * GST invoices carry a "Round Off" line; v1 declared `roundOff` in state and
 * never computed it, so the printed total could show stray paise.
 */
export function roundToNearestRupee(paise) {
  const p = round(paise);
  /**
   * Half rounds AWAY FROM ZERO, in both directions.
   *
   * `Math.round(-0.5)` is `-0`, so a credit note ending in exactly fifty paise
   * rounded towards zero while the invoice it reverses rounded away — the pair
   * then differed by one rupee and the party balance never came back to nil.
   * Rounding the magnitude and restoring the sign makes the two symmetrical.
   */
  const sign = p < 0 ? -1 : 1;
  const rounded = sign * Math.round(Math.abs(p) / PAISE_PER_RUPEE) * PAISE_PER_RUPEE;
  return { rounded, adjustment: rounded - p };
}


export const isNegative = (paise) => round(paise) < 0;
export const max = (a, b) => (round(a) > round(b) ? round(a) : round(b));
export const min = (a, b) => (round(a) < round(b) ? round(a) : round(b));
export const abs = (a) => Math.abs(round(a));

/**
 * Read a money field that may have been written by v1 (float rupees or a
 * string) or v2 (integer paise). Documents written by v2 carry
 * `schemaVersion >= 2`; anything older is treated as float rupees.
 *
 * This is what lets the rewritten app run against the existing database
 * without a destructive migration.
 */
export function readMoney(value, schemaVersion) {
  if (schemaVersion >= 2) return round(value);
  return toPaise(value);
}
